import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

/**
 * Custom SelectField component with enhanced functionality
 * Supports tooltips, validation, and custom styling
 *
 * Options format examples:
 * - Simple values: ["USD", "EUR", "GBP"]
 * - Object format: [{label: "US Dollar", value: "USD"}, {label: "Euro", value: "EUR"}]
 *
 * @param {Object} props - Component props
 * @param {string} props.label - Field label text
 * @param {string} props.name - Field name for form handling
 * @param {string} props.value - Current field value
 * @param {Function} props.onChange - Change handler function
 * @param {Array} props.options - Array of options to display in select
 *                              Can be simple values or objects with {label, value} format
 * @param {string} props.helperText - Helper text to display in tooltip
 * @param {number} props.maxLength - Maximum character length
 * @param {boolean} props.required - Whether the field is required
 * @param {string} props.error - Error message string (from validation)
 * @param {boolean} props.touched - Whether the field has been touched
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {boolean} props.fullWidth - Whether the field should take full width
 * @param {Object} props.sx - Custom styling props for FormControl
 * @param {Object} props.inputLabelSx - Custom styling props for InputLabel
 * @param {Object} props.selectProps - Additional props for Select component
 * @param {Object} props.menuItemProps - Additional props for MenuItem components
 */
const SelectField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  options = [],
  helperText,
  maxLength,
  required = false,
  error = "",
  touched = false,
  disabled = false,
  fullWidth = true,
  sx = {},
  inputLabelSx = {},
  selectProps = {},
  menuItemProps = {},
  ...otherProps
}) => {
  /**
   * Renders the label with optional tooltip
   * If helperText is provided, shows an info icon with tooltip
   */
  const renderLabel = () => {
    if (!label) return null;

    return (
      <>
        {label}
        {helperText && (
          <Tooltip title={helperText} arrow>
            <IconButton size="small" sx={{ padding: 0, marginLeft: "4px" }}>
              <InfoIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </>
    );
  };

  /**
   * Default InputLabel styling with required field styling
   * Shows red asterisk for required fields
   */
  const defaultInputLabelSx = {
    "& .MuiFormLabel-asterisk": {
      color: "red",
      fontSize: "1.2rem",
    },
    ...inputLabelSx,
  };

  /**
   * Default Select props
   */
  const defaultSelectProps = {
    labelId: `${name}-label`,
    name,
    label,
    value: value || "",
    onChange,
    onBlur,
    disabled,
    ...selectProps,
  };

  /**
   * Determine if field has error and should show error message
   * Error is shown only if field has been touched and has an error message
   */
  const hasError = touched && Boolean(error);
  const errorMessage = hasError ? error : "";

  /**
   * Check if option is an object with label and value properties
   */
  const isOptionObject = (option) => {
    return (
      option &&
      typeof option === "object" &&
      "label" in option &&
      "value" in option
    );
  };

  /**
   * Get the display value for an option
   * Handles both object format {label, value} and simple values
   */
  const getDisplayValue = (option) => {
    if (isOptionObject(option)) {
      return option.label;
    }
    if (typeof option === "boolean") {
      return option ? "Yes" : "No";
    }
    return String(option);
  };

  /**
   * Get the actual value for an option
   * Handles both object format {label, value} and simple values
   */
  const getOptionValue = (option) => {
    if (isOptionObject(option)) {
      return option.value;
    }
    return option;
  };

  /**
   * Get a unique key for an option
   * Handles both object format {label, value} and simple values
   */
  const getOptionKey = (option, index) => {
    if (isOptionObject(option)) {
      return option.value || `option-${index}`;
    }
    return option || `option-${index}`;
  };

  return (
    <FormControl
      fullWidth={fullWidth}
      required={required}
      disabled={disabled}
      error={hasError}
      sx={sx}
      {...otherProps}
    >
      <InputLabel id={`${name}-label`} sx={defaultInputLabelSx}>
        {renderLabel()}
      </InputLabel>
      <Select {...defaultSelectProps}>
        {options.map((option, index) => (
          <MenuItem
            key={getOptionKey(option, index)}
            value={getOptionValue(option)}
            {...menuItemProps}
          >
            {getDisplayValue(option)}
          </MenuItem>
        ))}
      </Select>
      {hasError && <FormHelperText error>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

export default SelectField;
